import { type FormEvent, useState, useEffect, useRef } from "react";
import type { GradeLevel, Student } from "@markeroo/shared";
import type { StudentFormData } from "@/features/students/types";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "@/components/Modal";
import { Select } from "@/components/Select";
import { fieldLabelClass, inputClass, smallMutedText } from "@/styles/variants";
import { buttonVariants } from "@markeroo/theme";
import { ui } from "@/strings";
import PiClipboardCheckStroke from "@markeroo/icons/src/stroke/files-&-folders/PiClipboardCheckStroke";
import PiRefreshStroke from "@markeroo/icons/src/stroke/arrows-&-chevrons/PiRefreshStroke";
import PiMultipleCrossCancelDefaultStroke from "@markeroo/icons/src/stroke/maths/PiMultipleCrossCancelDefaultStroke";
import { useNotification } from "@/providers/NotificationProvider";
import { notifications } from "@/strings";

const GRADE_LEVELS: GradeLevel[] = ["3", "4", "5", "6", "7", "8", "9", "10", "11", "12"];
const disabledInputClass = `${inputClass} disabled:bg-surface-page disabled:text-text-theme-muted disabled:cursor-not-allowed`;

function PortalLinkSection({
  portalKey,
  onRegenerate,
  onRevoke,
  isRegenerating,
  isRevoking,
}: {
  portalKey: string;
  onRegenerate?: () => void;
  onRevoke?: () => void;
  isRegenerating?: boolean;
  isRevoking?: boolean;
}) {
  const { show } = useNotification();
  const portalUrl = `${window.location.origin}/portal/${portalKey}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(portalUrl);
      show(notifications.clipboard.copied, "success");
    } catch {
      show(notifications.clipboard.copyFailed, "error");
    }
  };

  return (
    <div className="mt-4 rounded-lg border border-border bg-surface-secondary p-4">
      <h4 className="mb-2 text-sm font-medium text-text-theme-secondary">{ui.labels.studentPortal}</h4>
      <p className="mb-3 text-xs text-text-theme-muted">{ui.labels.studentPortalHelp}</p>
      <div className="flex items-center gap-2">
        <input
          type="text"
          readOnly
          value={portalUrl}
          className="flex-1 rounded border border-border bg-white px-3 py-1.5 text-sm text-text-theme-secondary"
          onClick={(e) => (e.target as HTMLInputElement).select()}
        />
        <button
          type="button"
          onClick={() => void handleCopy()}
          className="rounded p-2 text-text-theme-muted hover:bg-border hover:text-text-theme-secondary"
          title={ui.actions.copy}
        >
          <PiClipboardCheckStroke size={20} />
        </button>
      </div>
      <div className="mt-3 flex gap-2">
        {onRegenerate && (
          <button
            type="button"
            onClick={onRegenerate}
            disabled={isRegenerating || isRevoking}
            className="flex items-center gap-1 rounded px-2 py-1 text-xs text-text-theme-secondary hover:bg-border disabled:opacity-50"
            title={ui.labels.regeneratePortalKey}
          >
            <PiRefreshStroke size={16} className={isRegenerating ? "animate-spin" : ""} />
            {isRegenerating ? ui.actions.regenerating : ui.labels.regeneratePortalKey}
          </button>
        )}
        {onRevoke && (
          <button
            type="button"
            onClick={onRevoke}
            disabled={isRegenerating || isRevoking}
            className="flex items-center gap-1 rounded px-2 py-1 text-xs text-semantic-error hover:bg-semantic-error-bg disabled:opacity-50"
            title={ui.labels.revokePortalKey}
          >
            <PiMultipleCrossCancelDefaultStroke size={16} />
            {isRevoking ? ui.actions.revoking : ui.labels.revokePortalKey}
          </button>
        )}
      </div>
    </div>
  );
}

interface StudentFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: StudentFormData) => void;
  isSubmitting: boolean;
  defaultElaGradeLevel?: GradeLevel;
  initialStudent?: Student | null;
  initialName?: string;
  onRegeneratePortalKey?: () => void;
  onRevokePortalKey?: () => void;
  isRegeneratingPortalKey?: boolean;
  isRevokingPortalKey?: boolean;
  isGCSynced?: boolean;
}

export function StudentFormModal({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting,
  defaultElaGradeLevel,
  initialStudent,
  initialName,
  onRegeneratePortalKey,
  onRevokePortalKey,
  isRegeneratingPortalKey,
  isRevokingPortalKey,
  isGCSynced = false,
}: StudentFormModalProps) {
  const [name, setName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [elaGradeLevel, setElaGradeLevel] = useState<GradeLevel | undefined>(undefined);
  const [notes, setNotes] = useState("");
  const [isEll, setIsEll] = useState(false);
  const firstNameInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (initialStudent) {
      setName(initialStudent.name);
      setFirstName(initialStudent.firstName ?? "");
      setLastName(initialStudent.lastName ?? "");
      setEmail(initialStudent.email ?? "");
      setElaGradeLevel(initialStudent.elaGradeLevel);
      setNotes(initialStudent.notes ?? "");
      setIsEll(initialStudent.isEll ?? false);
    } else {
      setName(initialName ?? "");
      setFirstName("");
      setLastName("");
      setEmail("");
      setElaGradeLevel(undefined);
      setNotes("");
      setIsEll(false);
    }
  }, [initialStudent, initialName, isOpen]);

  useEffect(() => {
    if (!isOpen || !firstNameInputRef.current) {
      return;
    }
    const input = firstNameInputRef.current;
    input.focus();
    const length = input.value.length;
    try {
      input.setSelectionRange(length, length);
    } catch {
      // Some input types do not support selection range; ignore safely.
    }
  }, [isOpen, firstName]);

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // Pass email as null if empty string, otherwise pass the trimmed value
    const emailValue = email.trim() || null;
    // Build full name from first and last name
    const fullName = lastName.trim() ? `${firstName.trim()} ${lastName.trim()}` : firstName.trim();
    onSubmit({
      name: fullName || name,
      firstName: firstName.trim() || null,
      lastName: lastName.trim() || null,
      email: emailValue,
      elaGradeLevel,
      notes,
      isEll,
    });
  };

  const title = initialStudent ? `${ui.actions.edit} ${ui.labels.student}` : `${ui.actions.add} ${ui.labels.student}`;
  const submitLabel = initialStudent ? ui.actions.save : ui.actions.add;
  const submitLoadingLabel = initialStudent ? ui.actions.saving : ui.actions.submitting;

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalHeader title={title} onClose={onClose} />
      <ModalBody>
        <form onSubmit={handleSubmit} className="space-y-4" id="student-form">
          {isGCSynced ? (
            <label className={fieldLabelClass}>
              {ui.labels.name}
              <input
                type="text"
                value={name}
                className={disabledInputClass}
                disabled
                data-tutorial-target="student-name-input"
              />
              <p className={smallMutedText}>{ui.googleClassroom.managedByGoogleClassroom}</p>
            </label>
          ) : (
            <div className="flex gap-3">
              <label className={`${fieldLabelClass} flex-1`}>
                {ui.labels.firstName}
                <input
                  type="text"
                  ref={firstNameInputRef}
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className={inputClass}
                  required
                  data-tutorial-target="student-name-input"
                />
              </label>
              <label className={`${fieldLabelClass} flex-1`}>
                {ui.labels.lastName}
                {" ("}
                {ui.labels.optional}
                {")"}
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className={inputClass}
                />
              </label>
            </div>
          )}
          <label className={fieldLabelClass}>
            {ui.labels.email}
            {" ("}
            {ui.labels.optional}
            {")"}
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={isGCSynced ? disabledInputClass : inputClass}
              placeholder="student@example.com"
              disabled={isGCSynced}
            />
            {isGCSynced && <p className={smallMutedText}>{ui.googleClassroom.managedByGoogleClassroom}</p>}
          </label>
          <label className={fieldLabelClass}>
            {ui.labels.gradeLevel}
            {defaultElaGradeLevel ? ` (${ui.labels.optional})` : ""}
            <Select
              value={elaGradeLevel ?? ""}
              onChange={(e) =>
                setElaGradeLevel(e.target.value === "" ? undefined : (e.target.value as GradeLevel))
              }
              required={!defaultElaGradeLevel}
            >
              {defaultElaGradeLevel && (
                <option value="">
                  {ui.labels.useClassDefault}
                  {" ("}
                  {ui.formatting.gradeSingle(parseInt(defaultElaGradeLevel, 10))}
                  {")"}
                </option>
              )}
              {GRADE_LEVELS.map((grade) => (
                <option key={grade} value={grade}>
                  {ui.formatting.gradeSingle(parseInt(grade, 10))}
                </option>
              ))}
            </Select>
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={isEll}
              onChange={(e) => setIsEll(e.target.checked)}
              className="h-4 w-4 rounded border-border text-primary focus:ring-2 focus:ring-primary"
            />
            <span>
              {ui.labels.ellLabel}
              <span className="block text-xs text-text-theme-muted">{ui.labels.ellHelp}</span>
            </span>
          </label>
        </form>
        {/* Portal Link Section - shown when editing an existing student */}
        {initialStudent && (
          initialStudent.portalKey ? (
            <PortalLinkSection
              portalKey={initialStudent.portalKey}
              onRegenerate={onRegeneratePortalKey}
              onRevoke={onRevokePortalKey}
              isRegenerating={isRegeneratingPortalKey}
              isRevoking={isRevokingPortalKey}
            />
          ) : (
            <div className="mt-4 rounded-lg border border-border bg-surface-secondary p-4">
              <h4 className="mb-2 text-sm font-medium text-text-theme-secondary">{ui.labels.studentPortal}</h4>
              <p className="mb-3 text-xs text-text-theme-muted">{ui.labels.studentPortalHelp}</p>
              <button
                type="button"
                onClick={onRegeneratePortalKey}
                disabled={isRegeneratingPortalKey}
                className={buttonVariants.secondary}
              >
                {isRegeneratingPortalKey ? ui.actions.generating : ui.labels.generatePortalLink}
              </button>
            </div>
          )
        )}
      </ModalBody>
      <ModalFooter>
        <button type="button" onClick={onClose} className={buttonVariants.secondary}>
          {ui.actions.cancel}
        </button>
        <button
          type="submit"
          form="student-form"
          className={buttonVariants.primary}
          disabled={isSubmitting || (!firstName && !name)}
          data-tutorial-target="student-form-submit-button"
        >
          {isSubmitting ? submitLoadingLabel : submitLabel}
        </button>
      </ModalFooter>
    </Modal>
  );
}
